import React, { useEffect, useState } from "react"
import { Box, Skeleton, Stack, Typography } from "@mui/material"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { makeStyles } from "tss-react/mui"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLightbulb } from "@fortawesome/pro-solid-svg-icons"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { fetchAvailableRooms, FetchAvailableRoomsResponse, UserDetails } from "../../../../nonAdminUserApi"
import { DateTime } from "luxon"
import { faClock } from "@fortawesome/pro-light-svg-icons"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectKitchen } from "../../../../nonAdminUserSelectors"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { ErrorBoundary } from "react-error-boundary"
import { UserDashboardErrorFallbackCard } from "../UserDashboardErrorFallbackCard"

const useStyles = makeStyles()((theme) => ({
    container: {
        color: "black",
        backgroundColor: tailwindPalette.purple[200],
        borderRadius: ".75rem",
        padding: "1rem 1.25rem",
        [theme.breakpoints.up("xl")]: {
            padding: "1.25rem 2.25rem",
        },
    },
    section: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "1rem",
    },
    timeSection: {
        borderRadius: "0.625rem",
        padding: "1rem",
        backgroundColor: "#FFFFFF40",
        color: tailwindPalette.purple[900],
    },
    roomFinderButton: {
        border: "1px solid #CBD5E1 !important",
        backgroundColor: "white",
        color: tailwindPalette.slate[800],
        "&:hover": {
            backgroundColor: tailwindPalette.slate[100],
        },
    },
    schedule: {
        fontSize: "3.65rem",
        fontWeight: 500,
        textAlign: "center",
    },
    textColor: {
        color: "#000000DE",
    },
    icon: {
        marginRight: "0.5rem",
    },
    skeletonCard: {
        margin: "1rem auto",
        height: "3.5rem",
        borderRadius: ".75rem",
        width: "20%",
    },
    errorCard: {
        backgroundColor: "transparent",
        boxShadow: "none",
    },
    errorContent: {
        paddingLeft: 0,
        paddingRight: 0,
    },
}))

const transformAvailableRooms = (data: FetchAvailableRoomsResponse) => {
    return data.availableRooms ?? {}
}

const getCurrentTimeSlot = () => {
    const now = DateTime.now()
    const startHour = now.startOf("hour")
    const endHour = startHour.plus({ hours: 1 })

    return {
        timeSlot: `${startHour.toFormat("HH:mm")} - ${endHour.toFormat("HH:mm")}`,
        startDateTime: startHour.toISO(),
        endDateTime: endHour.toISO(),
    }
}

function SafeFindRoom({ onOpenRoomFinder }: { onOpenRoomFinder: () => void }) {
    const { classes, cx } = useStyles()
    const [timeSlot, setTimeSlot] = useState(getCurrentTimeSlot().timeSlot)
    const [startDateTime, setStartDateTime] = useState<string | null>(getCurrentTimeSlot().startDateTime)
    const [endDateTime, setEndDateTime] = useState<string | null>(getCurrentTimeSlot().endDateTime)

    const selectedKitchen = useAppSelector(selectKitchen)

    useEffect(() => {
        const interval = setInterval(() => {
            const { timeSlot: newTimeSlot, startDateTime: newStart, endDateTime: newEnd } = getCurrentTimeSlot()
            setTimeSlot(newTimeSlot)
            setStartDateTime(newStart)
            setEndDateTime(newEnd)
        }, 60000)

        return () => clearInterval(interval)
    }, [])

    const { data: rooms, isLoading } = useApiQuery(fetchAvailableRooms, {
        queryName: UserDetails.USER_AVAILABLE_ROOMS,
        dependencies: {
            locationId: selectedKitchen?.id ?? 0,
            startDateTime: startDateTime,
            endDateTime: endDateTime,
        },
        select: transformAvailableRooms,
        refetchInterval: 60000,
        useErrorBoundary: true,
    })

    return (
        <>
            <Box className={cx(classes.section)}>
                <LocalizedStrict id="user-homeroom-finder-title">
                    <Typography fontWeight={500} variant="h5">
                        Room finder
                    </Typography>
                </LocalizedStrict>

                <LocalizedStrict id="user-home-room-finder-subtitle">
                    <Typography variant="body1" className={classes.textColor}>
                        Your intelligent companion for finding resources.
                    </Typography>
                </LocalizedStrict>
                <Box>
                    <LocalizedStrict id="user-home-room-finder-find-a-room-button">
                        <StandardButton
                            fullWidth={false}
                            className={classes.roomFinderButton}
                            startIcon={<FontAwesomeIcon icon={faLightbulb} />}
                            onClick={onOpenRoomFinder}
                        >
                            Find a room
                        </StandardButton>
                    </LocalizedStrict>
                </Box>
            </Box>

            <Box className={cx(classes.section, classes.timeSection)}>
                <ErrorBoundary fallback={<UserDashboardErrorFallbackCard />}>
                    <Stack spacing="0.5rem">
                        <Box>
                            {isLoading ? (
                                <Skeleton variant="rectangular" className={classes.skeletonCard} />
                            ) : (
                                <Typography className={classes.schedule}>{rooms?.count || 0}</Typography>
                            )}
                            <LocalizedStrict id="user-home-room-finder-available-rooms">
                                <Typography variant="subtitle1" className={classes.textColor} textAlign="center">
                                    available rooms
                                </Typography>
                            </LocalizedStrict>
                            <Typography color="#00000099" textAlign="center">
                                <FontAwesomeIcon className={classes.icon} icon={faClock} />
                                {timeSlot}
                            </Typography>
                        </Box>
                    </Stack>
                </ErrorBoundary>
            </Box>
        </>
    )
}
export function FindRoom(props: { onOpenRoomFinder: () => void }) {
    const { classes } = useStyles()
    return (
        <Stack direction={{ xs: "column", md: "row" }} className={classes.container} marginBottom="3rem" spacing="2rem">
            <QueryErrorBoundary Fallback={RoomFinderFallback}>
                <SafeFindRoom {...props} />
            </QueryErrorBoundary>
        </Stack>
    )
}

const RoomFinderFallback = () => {
    const { classes } = useStyles()

    return (
        <Box marginBottom="3rem">
            <UserDashboardErrorFallbackCard classNames={classes.errorCard} contentClassNames={classes.errorContent}>
                <LocalizedStrict id="user-homeroom-finder-title">
                    <Typography fontWeight={500} variant="h5">
                        Room finder
                    </Typography>
                </LocalizedStrict>
            </UserDashboardErrorFallbackCard>
        </Box>
    )
}
